import React, { useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CalendarRange, AlertTriangle } from 'lucide-react';
import Panel from '../ui/Panel';
import EmptyState from '../ui/EmptyState';
import {
  displayName,
  formatDateShort,
  formatDate,
} from './utilizationHelpers';

const DAY_MS = 86400000;

const parseDate = (value) => {
  if (!value) return null;
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
};

/**
 * Compute the start/end window used to render the timeline rows:
 * start = earliest allocation start - 7 days, end = latest allocation end + 7 days.
 * Falls back to a 90-day window around "now" when there are no allocations.
 */
const computeRange = (users) => {
  let min = null;
  let max = null;
  users.forEach((user) => {
    (user.allocations || []).forEach((a) => {
      const s = parseDate(a.start_date);
      const e = parseDate(a.end_date);
      if (s && (!min || s < min)) min = s;
      if (e && (!max || e > max)) max = e;
    });
  });
  if (!min || !max) {
    const now = Date.now();
    return {
      start: new Date(now - 30 * DAY_MS),
      end: new Date(now + 60 * DAY_MS),
    };
  }
  return {
    start: new Date(min.getTime() - 7 * DAY_MS),
    end: new Date(max.getTime() + 7 * DAY_MS),
  };
};

const palette = [
  'bg-primary-500',
  'bg-sky-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-purple-500',
  'bg-rose-500',
  'bg-indigo-500',
  'bg-teal-500',
];

const colorForProject = (projectId, projectColorMap) => {
  if (!projectColorMap.has(projectId)) {
    projectColorMap.set(projectId, palette[projectColorMap.size % palette.length]);
  }
  return projectColorMap.get(projectId);
};

/**
 * Floating hover-pill with the full allocation details (project, phase,
 * dates, %). Rendered through a portal into ``document.body`` so the
 * ``overflow-x-auto`` panel wrapper never clips it, and positioned relative
 * to the triggering bar via its bounding rect.
 */
const BarTooltip = ({ allocation, anchorRect }) => {
  if (!anchorRect) return null;
  const left = anchorRect.left + anchorRect.width / 2;
  // Prefer sitting above the bar, but flip to below when there isn't room so
  // rows near the top of the viewport don't push the tooltip off-screen.
  const spaceAbove = anchorRect.top;
  const placeBelow = spaceAbove < 72;
  const top = placeBelow ? anchorRect.bottom + 8 : anchorRect.top - 8;
  return createPortal(
    <div
      data-testid={`timeline-bar-tooltip-${allocation.id}`}
      role="tooltip"
      style={{
        position: 'fixed',
        left,
        top,
        transform: placeBelow
          ? 'translate(-50%, 0)'
          : 'translate(-50%, -100%)',
        zIndex: 1000,
        pointerEvents: 'none',
      }}
      className="bg-secondary-900/95 dark:bg-secondary-100 text-white dark:text-secondary-900 text-xs rounded-md shadow-xl px-2.5 py-1.5 whitespace-nowrap min-w-[160px] max-w-[320px]"
    >
      <div className="font-semibold text-sm">
        {allocation.project_name || 'Project'}
      </div>
      {allocation.phase && (
        <div className="text-[11px] opacity-80 mt-0.5">
          Phase: {allocation.phase}
        </div>
      )}
      <div className="text-[11px] opacity-80">
        {formatDate(allocation.start_date)} → {formatDate(allocation.end_date)}
      </div>
      {allocation.allocation_percent != null && (
        <div className="text-[11px] opacity-80">
          {allocation.allocation_percent}% allocated
        </div>
      )}
      {allocation.client && (
        <div className="text-[11px] opacity-80">Client: {allocation.client}</div>
      )}
    </div>,
    document.body,
  );
};

const Bar = ({ allocation, rangeStart, rangeMs, color, conflict }) => {
  const s = parseDate(allocation.start_date);
  const e = parseDate(allocation.end_date);
  const barRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);

  // Native title kept as a graceful fallback for keyboard / assistive users
  // (the custom pill lives on hover + focus-visible only).
  const nativeTitle = `${allocation.project_name || 'Project'}${
    allocation.phase ? ` • ${allocation.phase}` : ''
  } • ${formatDate(allocation.start_date)} → ${formatDate(
    allocation.end_date,
  )}${
    allocation.allocation_percent != null
      ? ` • ${allocation.allocation_percent}%`
      : ''
  }`;

  const showTip = useCallback(() => {
    if (barRef.current) {
      setAnchorRect(barRef.current.getBoundingClientRect());
    }
  }, []);
  const hideTip = useCallback(() => setAnchorRect(null), []);

  if (!s || !e) return null;
  const left = Math.max(0, (s.getTime() - rangeStart) / rangeMs) * 100;
  const width = Math.max(
    1,
    ((Math.min(e.getTime(), rangeStart + rangeMs) - Math.max(s.getTime(), rangeStart)) /
      rangeMs) *
      100,
  );

  return (
    <>
      <div
        ref={barRef}
        data-testid={`timeline-bar-${allocation.id}`}
        className={`absolute top-1 bottom-1 rounded-md shadow-sm text-[10px] font-medium text-white flex items-center justify-center px-1 overflow-hidden cursor-pointer transition-transform duration-100 hover:brightness-110 hover:ring-2 hover:ring-white/40 ${color} ${
          conflict ? 'ring-2 ring-red-500 ring-offset-1' : ''
        }`}
        style={{ left: `${left}%`, width: `${width}%` }}
        title={nativeTitle}
        tabIndex={0}
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
        onFocus={showTip}
        onBlur={hideTip}
      >
        <span className="truncate">
          {allocation.project_name}
          {allocation.allocation_percent != null
            ? ` ${allocation.allocation_percent}%`
            : ''}
        </span>
      </div>
      {anchorRect && (
        <BarTooltip allocation={allocation} anchorRect={anchorRect} />
      )}
    </>
  );
};

const UserRow = ({ user, rangeStart, rangeMs, projectColorMap }) => {
  const conflictAllocIds = new Set();
  (user.conflicts || []).forEach((c) => {
    (c.allocation_ids || []).forEach((id) => conflictAllocIds.add(id));
  });

  return (
    <div
      data-testid={`timeline-row-${user.id}`}
      className={`grid grid-cols-[200px_minmax(0,1fr)] gap-3 items-center py-2 border-t border-secondary-100 dark:border-secondary-700/70 ${
        user.is_over_allocated ? 'bg-red-50/40 dark:bg-red-900/10' : ''
      }`}
    >
      <div className="min-w-0 pr-2">
        <div className="text-sm font-medium text-secondary-900 dark:text-white truncate">
          {displayName(user)}
        </div>
        <div className="text-[11px] text-secondary-500 dark:text-secondary-400 flex items-center gap-2">
          {user.cohort && (
            <span className="truncate">{user.cohort}</span>
          )}
          {user.free_date && (
            <span className="truncate">
              free {formatDateShort(user.free_date)}
            </span>
          )}
          {user.is_over_allocated && (
            <span className="inline-flex items-center gap-0.5 text-red-600 dark:text-red-400">
              <AlertTriangle className="h-3 w-3" />
              over
            </span>
          )}
        </div>
      </div>
      <div className="relative h-7">
        {(user.allocations || []).map((a) => (
          <Bar
            key={a.id}
            allocation={a}
            rangeStart={rangeStart}
            rangeMs={rangeMs}
            color={colorForProject(a.project_id, projectColorMap)}
            conflict={conflictAllocIds.has(a.id)}
          />
        ))}
      </div>
    </div>
  );
};

const TimelineHeader = ({ start, end }) => {
  const months = useMemo(() => {
    const result = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const total = end.getTime() - start.getTime();
    while (cursor <= end) {
      const monthStart = new Date(cursor);
      const monthEnd = new Date(
        cursor.getFullYear(),
        cursor.getMonth() + 1,
        0,
      );
      const effectiveStart = Math.max(monthStart.getTime(), start.getTime());
      const effectiveEnd = Math.min(monthEnd.getTime(), end.getTime());
      if (effectiveEnd > effectiveStart) {
        const left = ((effectiveStart - start.getTime()) / total) * 100;
        const width = ((effectiveEnd - effectiveStart) / total) * 100;
        result.push({
          label: monthStart.toLocaleDateString(undefined, {
            month: 'short',
            year: '2-digit',
          }),
          left,
          width,
        });
      }
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return result;
  }, [start, end]);

  return (
    <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-3 pb-2 border-b border-secondary-200 dark:border-secondary-700">
      <div className="text-[11px] text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
        Employee
      </div>
      <div className="relative h-5">
        {months.map((m) => (
          <div
            key={m.label}
            className="absolute top-0 bottom-0 text-[10px] text-secondary-500 dark:text-secondary-400 border-l border-secondary-200 dark:border-secondary-700 pl-1"
            style={{ left: `${m.left}%`, width: `${m.width}%` }}
          >
            {m.label}
          </div>
        ))}
      </div>
    </div>
  );
};

const TimelineView = ({ users = [] }) => {
  const withAllocs = useMemo(
    () => users.filter((u) => (u.allocations || []).length > 0),
    [users],
  );

  const { start, end } = useMemo(() => computeRange(withAllocs), [withAllocs]);
  const rangeMs = end.getTime() - start.getTime();
  const projectColorMap = new Map();

  if (!withAllocs.length) {
    return (
      <Panel>
        <EmptyState
          icon={CalendarRange}
          title="No allocations to show"
          description="Create allocations from the People tab or a project card to populate the timeline."
        />
      </Panel>
    );
  }

  return (
    <Panel className="overflow-x-auto" data-testid="resource-timeline-view">
      <div className="min-w-[640px]">
        <TimelineHeader start={start} end={end} />
        {withAllocs.map((user) => (
          <UserRow
            key={user.id}
            user={user}
            rangeStart={start.getTime()}
            rangeMs={rangeMs}
            projectColorMap={projectColorMap}
          />
        ))}
      </div>
    </Panel>
  );
};

export default TimelineView;
